/**
 * Step 5: Review
 * Big video player, variant tabs (ShadCN Tabs), compact campaign summary (Card).
 * Binary choice: approve or regenerate. Counter visible ("2 left").
 * Optional one-line feedback on regeneration.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import SelfServeHeader from '@/components/mediaplan/SelfServeHeader';
import ErrorCard from '@/components/mediaplan/ErrorCard';
import { ReviewSkeleton } from '@/components/mediaplan/ScreenSkeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  Play,
  CheckCircle2,
  RefreshCw,
  Calendar,
  Clock,
  Users,
  MapPin,
  Loader2,
  MessageSquare,
} from 'lucide-react';
import mediaPlanApi from '@/services/mediaPlanApi';
import useMediaPlanFlow from '@/hooks/useMediaPlanFlow';

const MAX_REGENERATIONS = 3;

export default function CampaignReview() {
  const navigate = useNavigate();
  const { planId } = useParams();
  const flow = useMediaPlanFlow();

  const campaignId = flow.campaignId || planId;

  const [variants, setVariants] = useState(null);
  const [plan, setPlan] = useState(flow.plan || null);
  const [business, setBusiness] = useState(flow.business || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeVariant, setActiveVariant] = useState(null);
  const [regenerationsLeft, setRegenerationsLeft] = useState(2);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // Load data on mount
  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        // Load plan if not in flow context
        let planData = flow.plan;
        if (!planData && planId) {
          planData = await mediaPlanApi.getPlan(planId);
          if (!cancelled) setPlan(planData);
        }

        // Load business if not in flow context
        if (!flow.business && planData) {
          // Business info may come from getCampaign
          try {
            const campaign = await mediaPlanApi.getCampaign(campaignId);
            if (!cancelled && campaign.businessName) {
              setBusiness({ name: campaign.businessName, ...campaign.business });
            }
          } catch {
            // Non-critical; business name may come from plan
          }
        }

        // Load variants
        const variantData = await mediaPlanApi.getCampaignVariants(campaignId);
        if (!cancelled) {
          setVariants(variantData);
          if (variantData.length > 0) {
            setActiveVariant(variantData[0].id);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load review data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, [campaignId, planId]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeVariantIndex = variants ? variants.findIndex((v) => v.id === activeVariant) : -1;
  const currentVariant = variants?.[activeVariantIndex] || variants?.[0];
  const businessName = business?.name || flow.business?.name || 'Your Business';

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      const result = await mediaPlanApi.approveCampaign(campaignId);
      flow.setCampaign(result);
      toast.success('Campaign approved! Launching now...');
      navigate(`/campaign/${planId}/live`);
    } catch (err) {
      toast.error(err.message || 'Failed to approve campaign');
      setIsApproving(false);
    }
  };

  const handleRegenerate = async () => {
    if (regenerationsLeft <= 0) return;
    setIsRegenerating(true);
    try {
      const updatedVariants = await mediaPlanApi.regenerateVariants(campaignId, feedback);
      setVariants(updatedVariants);
      setRegenerationsLeft((prev) => prev - 1);
      setShowFeedback(false);
      setFeedback('');
      if (updatedVariants.length > 0) {
        setActiveVariant(updatedVariants[0].id);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to regenerate variants');
    } finally {
      setIsRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <SelfServeHeader businessName={businessName} />
        <div className="max-w-4xl mx-auto px-6 py-8">
          <ReviewSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <SelfServeHeader businessName={businessName} />
        <ErrorCard
          message={error}
          onRetry={() => window.location.reload()}
          fallbackPath="/dashboard"
          fallbackLabel="Back to Dashboard"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SelfServeHeader businessName={businessName} />

      <div className="max-w-4xl mx-auto px-6 py-8 pb-36">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          {/* Video Player */}
          <div>
            {variants && variants.length > 1 && (
              <Tabs value={activeVariant} onValueChange={setActiveVariant} className="mb-4">
                <TabsList>
                  {variants.map((variant) => (
                    <TabsTrigger key={variant.id} value={variant.id}>
                      Variant: {variant.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}

            <div
              className="relative bg-slate-900 rounded-xl overflow-hidden aspect-video group cursor-pointer"
              onClick={() => setIsPlaying(!isPlaying)}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
                {isRegenerating ? (
                  <div className="text-center text-white">
                    <Loader2 className="h-10 w-10 animate-spin mx-auto mb-3 text-coral" />
                    <div className="text-sm font-medium">Regenerating your ad...</div>
                    <div className="text-xs text-slate-400 mt-1">This will take about 2 minutes</div>
                  </div>
                ) : (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-br from-coral/20 via-transparent to-teal/20" />
                    <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-6 left-6 text-white">
                      <div className="text-2xl font-bold">{businessName}</div>
                      <div className="text-sm text-white/70 mt-0.5">{plan?.creative?.message}</div>
                    </div>
                    <div className={cn(
                      'w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-md transition-all',
                      'group-hover:scale-110 group-hover:bg-white'
                    )}>
                      <Play className="h-7 w-7 text-slate-900 ml-1" />
                    </div>
                    <Badge className="absolute top-4 right-4 bg-black/50 text-white border-0 backdrop-blur-sm">
                      0:{currentVariant?.duration || 15}
                    </Badge>
                  </>
                )}
              </div>
            </div>

            {currentVariant && (
              <div className="mt-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">{currentVariant.label} Version</div>
                  <div className="text-xs text-muted-foreground">{currentVariant.description}</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {currentVariant.resolution} -- {currentVariant.duration}s
                </div>
              </div>
            )}
          </div>

          {/* Campaign Summary Sidebar */}
          <div className="space-y-4">
            {plan && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Campaign Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2.5 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="font-medium">{plan.duration}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(plan.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(plan.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="font-medium">{plan.audience.radius}mi radius</div>
                      <div className="text-xs text-muted-foreground">{plan.audience.households.toLocaleString()} households</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="font-medium">Peak: {plan.schedule.peak}</div>
                      <div className="text-xs text-muted-foreground">CTV primetime placement</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="font-medium">{plan.audience.segments.length} audience segments</div>
                      <div className="text-xs text-muted-foreground">{plan.audience.segments[1]?.label}</div>
                    </div>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Budget</span>
                    <span className="text-lg font-bold">${plan.budget.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted border-0 shadow-none">
              <CardContent className="py-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-muted-foreground">Regenerations</span>
                  <span className="text-xs font-bold">
                    {regenerationsLeft} of {MAX_REGENERATIONS} remaining
                  </span>
                </div>
                <div className="flex gap-1">
                  {Array.from({ length: MAX_REGENERATIONS }).map((_, i) => (
                    <div
                      key={i}
                      className={cn(
                        'flex-1 h-1.5 rounded-full',
                        i < regenerationsLeft ? 'bg-coral' : 'bg-border'
                      )}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {showFeedback && (
              <Card className="animate-[fadeInUp_0.2s_ease]">
                <CardContent className="py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs font-semibold">What should we change?</span>
                  </div>
                  <Input
                    placeholder="e.g., Make it more energetic, change the music..."
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    className="mb-3"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-coral hover:bg-coral/90 text-white"
                      onClick={handleRegenerate}
                      disabled={isRegenerating}
                    >
                      {isRegenerating ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      Regenerate
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setShowFeedback(false); setFeedback(''); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-[0_-4px_16px_rgba(0,0,0,0.06)] z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            {!showFeedback && regenerationsLeft > 0 && !isRegenerating && (
              <Button variant="outline" className="gap-1.5" onClick={() => setShowFeedback(true)}>
                <RefreshCw className="h-4 w-4" />
                Regenerate
                <span className="text-xs text-muted-foreground ml-0.5">({regenerationsLeft} left)</span>
              </Button>
            )}
            {regenerationsLeft === 0 && (
              <span className="text-xs text-muted-foreground">No regenerations remaining</span>
            )}
          </div>
          <Button
            className="bg-coral hover:bg-coral/90 text-white shadow-[0_2px_10px_rgba(255,107,107,0.35)] hover:scale-[1.02] transition-all h-11 px-8 text-sm font-semibold"
            onClick={handleApprove}
            disabled={isApproving || isRegenerating}
          >
            {isApproving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Launching...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Approve and Launch
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
